/**
 * DraftPlanPrint — the paper version of a plan. Its own markup, same data.
 *
 * 🔴 WHY THIS EXISTS INSTEAD OF A PRINT STYLESHEET. The first attempt styled
 * the live DOM for print and it failed in preview while passing build, lint and
 * arithmetic: the summary clipped off the right edge, the budget <input>s
 * rendered as ghosted partial values, the stats icons survived the hide list,
 * and it still broke to a second page at BN2. Number inputs, flex cells, images
 * and a dark surface all fight the printer, and no amount of `display: none`
 * un-fights them. So print gets its own tree: a plain table, no inputs, no
 * images, no flex, white paper and black ink.
 *
 * EXACTLY ONE OF THE TWO TREES IS EVER VISIBLE. On screen this component is
 * display:none and the live grid renders; in print the live grid (.dp-root) is
 * display:none and this renders. Never both.
 *
 * This is a sheet you carry to a draft, not a screenshot of the app — hence
 * white background and black text rather than the dark theme. The only colour
 * is the player's name, tinted by position, which survives greyscale printing
 * because the name and price carry the information regardless.
 */

import React from "react";
import { theme } from "../../data/theme";
import { primaryOf, slotBudget, BUCKET_WIDTH } from "../../hooks/usePlanState";
import { formatPpgTotal } from "./ppgTotals";
import "../../styles/draftPlanPrint.css";

const money = (n) => `$${Math.round(Number(n) || 0)}`;

/**
 * One target as plain text: bold name (position-tinted) then its price.
 *
 * 🔴 A STAR IS A GLYPH HERE, NOT A TINT. The screen marks your guys with a
 * coloured edge and a raised surface; neither survives a printer — a
 * background tint is the first thing "print backgrounds off" throws away, and
 * this sheet is deliberately black on white anyway. A ★ before the name
 * survives greyscale, survives a fax, and survives being photocopied at the
 * table, which is most of the point of carrying the sheet at all.
 */
function Cell({ target, starred }) {
  if (!target) return <td className="dpp-cell dpp-empty">&mdash;</td>;
  return (
    <td className="dpp-cell">
      {starred && <span className="dpp-star">&#9733;</span>}
      <span className="dpp-name" style={{ color: theme.colors.pos[target.pos] || "#000" }}>
        {target.name}
      </span>
      <span className="dpp-pos">{target.pos}</span>
      <span className="dpp-price">{money(target.price)}</span>
    </td>
  );
}

function SlotRows({ slots, buckets, isStarred }) {
  return slots.map((slot) => {
    const bucket = buckets[slot.id];
    const cells = (bucket && bucket.cells) || new Array(BUCKET_WIDTH).fill(null);
    const primary = primaryOf(bucket);   // budget line only; columns are positional
    return (
      <tr key={slot.id}>
        <th scope="row" className="dpp-slot">
          <span className="dpp-slot-label">{slot.label}</span>
          {/* The earmark, or the implicit primary price when none is set. Plain
              text — the screen's number input has no business on paper. */}
          <span className="dpp-slot-budget">{primary || (bucket && bucket.budget != null) ? money(slotBudget(bucket)) : ""}</span>
        </th>
        {/* 🔴 STRICTLY POSITIONAL. Cell c is column c, because a column is now a
            whole roster (Plan A/B/C/D) rather than "primary then backups". The
            old primary-first reorder would move a Plan B player into the Plan A
            column on paper, which is exactly the thing the sheet must not do. A
            fallen-through primary therefore prints as an em dash in column A,
            which is accurate to what the column holds. */}
        {Array.from({ length: BUCKET_WIDTH }, (_, i) => (
          <Cell
            key={i}
            target={cells[i] || null}
            starred={!!cells[i] && !!isStarred && isStarred(cells[i].key)}
          />
        ))}
      </tr>
    );
  });
}

/**
 * A group's bottom line, spanning the player columns.
 *
 * 🔴 BENCH PPG IS ON THE SHEET even though bench points score you nothing.
 * At a live auction it answers the question you actually ask of a bench — how
 * much production is sitting behind an injury — which is worth more on paper
 * than the strict scoring correctness of omitting it. The summary tile upstairs
 * stays starters-only; this is a different question.
 */
function SubtotalRow({ label, group, ppg, unprojected = 0, cols }) {
  // The label and its figures share ONE cell spanning SLOT + PRIMARY, left
  // aligned. Right-aligning the numbers across the backup columns put a group's
  // name and its totals at opposite edges of the sheet, which is a long eye
  // travel for a line you glance at. Two columns is enough room for all three
  // figures; the remaining backup columns stay empty but still carry the rule.
  //
  // The committed count is deliberately NOT here. It is on screen already, and
  // at a draft table what you want off the paper is the money and the points,
  // not a completion tally.
  return (
    <tr className="dpp-subtotal">
      <th scope="row" colSpan={2} className="dpp-subtotal-cell">
        <span className="dpp-sub-label">{label}</span>
        <span className="dpp-sub-figs">
          {money(group.planned)} planned &middot; {money(group.targeted)} targeted
          {ppg > 0 || unprojected > 0 ? ` · ${formatPpgTotal(ppg, unprojected)} PPG` : ""}
        </span>
      </th>
      {Array.from({ length: cols - 1 }, (_, i) => <td key={i} />)}
    </tr>
  );
}

export default function DraftPlanPrint({
  planName,
  leagueName,
  cap,
  planned,
  targeted,
  left,
  startersPpg,
  startersUnprojected = 0,
  rosterSlots,
  buckets,
  byGroup,
  groupPpg,
  columnTotals,
  columnLabels,
  isStarred,
}) {
  const starters = rosterSlots.filter((s) => s.pos !== "BN");
  const bench = rosterSlots.filter((s) => s.pos === "BN");
  const cols = BUCKET_WIDTH; // PRIMARY + 3 backups

  return (
    <div className="dpp-root">
      <div className="dpp-header">
        <strong>{planName || "Draft Plan"}</strong>
        {leagueName ? <> &middot; {leagueName}</> : null}
        {" · "}
        {money(cap)} budget &middot; planned {money(planned)} &middot; targeted {money(targeted)}
        {" · "}left {money(left)}
        {/* 🔴 The unprojected count comes to PAPER too. A draft-day total that
            silently omits a player is worse here than on screen: there is no
            hover to investigate and no way to ask. */}
        {startersPpg > 0 || startersUnprojected > 0
          ? <> &middot; starters {formatPpgTotal(startersPpg, startersUnprojected)} PPG</>
          : null}
      </div>

      <table className="dpp-table">
        <thead>
          {/* Each column is a whole roster, so each header carries that
              roster's own cost and starters PPG. The drop from A to D is the
              depth behind your targets and is deliberately not smoothed. */}
          <tr>
            <th className="dpp-slot">SLOT</th>
            {(columnLabels || ["PLAN A", "PLAN B", "PLAN C", "PLAN D"]).map((label, i) => {
              const t = columnTotals && columnTotals[i];
              return (
                <th key={label}>
                  {label}
                  {t && t.count > 0 && (
                    <span className="dpp-col-total">
                      {" "}${Math.round(t.cost)}{t.ppg > 0 || t.unprojected > 0 ? ` · ${formatPpgTotal(t.ppg, t.unprojected)}` : ""}
                    </span>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          <tr className="dpp-divider"><td colSpan={cols + 1}>STARTERS</td></tr>
          <SlotRows slots={starters} buckets={buckets} isStarred={isStarred} />
          {byGroup && (
            <SubtotalRow label="STARTERS" group={byGroup.starters} ppg={groupPpg ? groupPpg.starters.ppg : 0} unprojected={groupPpg ? groupPpg.starters.unprojected : 0} cols={cols} />
          )}
          {bench.length > 0 && (
            <tr className="dpp-divider"><td colSpan={cols + 1}>BENCH</td></tr>
          )}
          <SlotRows slots={bench} buckets={buckets} isStarred={isStarred} />
          {bench.length > 0 && byGroup && (
            <SubtotalRow label="BENCH" group={byGroup.bench} ppg={groupPpg ? groupPpg.bench.ppg : 0} unprojected={groupPpg ? groupPpg.bench.unprojected : 0} cols={cols} />
          )}
        </tbody>
      </table>
    </div>
  );
}
